import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Phone, User, Send, CheckCircle2, History, ArrowLeft, ShoppingBag, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "https://api.shopkwality.in";
const WHATSAPP_NUMBER = "919986333180";
const STORAGE_KEY = "ktz_enquiries";
const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 30 * 6;

export default function CartPage() {
  const [cart, setCart] = useState([]);
  const [userInfo, setUserInfo] = useState({ name: "", mobile: "" });
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [history, setHistory] = useState([]);
  const [submittedNo, setSubmittedNo] = useState("");
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [showCustomForm, setShowCustomForm] = useState(false);

  const handleAddCustom = (e) => {
    e.preventDefault();
    if (!customName.trim()) {
      alert("Please enter a name for your custom product.");
      return;
    }
    const newItem = {
      id: `custom_${Date.now()}`,
      name: customName.trim(),
      product_desc: customDesc.trim() || null,
      is_custom: true,
      quantity: 1
    };
    const updated = [...cart, newItem];
    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));

    // Reset form
    setCustomName("");
    setCustomDesc("");
    setShowCustomForm(false);
  };

  /* ---------------- LOAD CART ---------------- */

  useEffect(() => {
    const load = () => {
      setCart(JSON.parse(localStorage.getItem("cart") || "[]"));
    };
    load();
    window.addEventListener("cart-updated", load);
    return () => window.removeEventListener("cart-updated", load);
  }, []);

  /* ---------------- LOAD ENQUIRY HISTORY ---------------- */

  useEffect(() => {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const now = Date.now();

    const cleaned = raw.filter(
      e => now - new Date(e.created_at).getTime() < SIX_MONTHS_MS
    );

    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    setHistory(cleaned);
  }, []);

  /* ---------------- QUANTITY CONTROLS ---------------- */

  const updateQty = (id, delta) => {
    const updated = cart
      .map(p =>
        p.id === id ? { ...p, quantity: p.quantity + delta } : p
      )
      .filter(p => p.quantity > 0);

    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));
  };

  const handleQtyChange = (id, val) => {
    const updated = [...cart];
    const existing = updated.find(p => p.id === id);
    if (!existing) return;

    if (val === "") {
      existing.quantity = "";
      setCart(updated);
      localStorage.setItem("cart", JSON.stringify(updated.map(p => p.quantity === "" ? { ...p, quantity: 1 } : p)));
      window.dispatchEvent(new Event("cart-updated"));
      return;
    }

    const parsed = parseInt(val, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      existing.quantity = parsed;
      let finalUpdated = updated;
      if (parsed === 0) {
        finalUpdated = updated.filter(p => p.id !== id);
      }
      setCart(finalUpdated);
      localStorage.setItem("cart", JSON.stringify(finalUpdated));
      window.dispatchEvent(new Event("cart-updated"));
    }
  };

  const handleQtyBlur = (id, val) => {
    const parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed <= 0) {
      const updated = [...cart];
      const existing = updated.find(p => p.id === id);
      if (existing) {
        existing.quantity = 1;
        setCart(updated);
        localStorage.setItem("cart", JSON.stringify(updated));
        window.dispatchEvent(new Event("cart-updated"));
      }
    }
  };

  /* ---------------- WHATSAPP FORMATTER ---------------- */

  const buildWhatsAppMessage = () => {
    let msg = `*KWALITY TECH ZONE*\n`;
    msg += `Premium Electronics Catalog Enquiry\n\n`;
    msg += `─────────────────────\n\n`;
    msg += `*Request Details:*\n`;
    cart.forEach((p, i) => {
      if (p.is_custom) {
        msg += `${i + 1}. *[CUSTOM]* *${p.name}* (${p.quantity} pcs)\n`;
        if (p.product_desc) {
          msg += `   _Details: ${p.product_desc}_\n`;
        }
      } else {
        msg += `${i + 1}. *${p.name}* (${p.quantity} pcs)\n`;
      }
    });
    msg += `\n─────────────────────\n\n`;
    msg += `👤 *Client Name:* ${userInfo.name}\n`;
    msg += `📞 *Mobile Number:* ${userInfo.mobile}\n\n`;
    msg += `Please provide quotation and availability details. Thank you!`;
    return encodeURIComponent(msg);
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (cart.length === 0) {
      alert("Your quotation cart is currently empty.");
      return;
    }
    if (!userInfo.name.trim() || !userInfo.mobile.trim()) {
      alert("Please enter both your name and mobile number to proceed.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${API}/enquiries`, {
        name: userInfo.name.trim(),
        mobile: userInfo.mobile.trim(),
        items: cart.map(p => {
          if (p.is_custom) {
            return {
              product_id: null,
              product_name: p.name,
              product_desc: p.product_desc || null,
              is_custom: true,
              quantity: p.quantity
            };
          } else {
            return {
              product_id: p.id.toString(),
              quantity: p.quantity
            };
          }
        })
      });

      const nextNo = res.data.enquiry_no || `KTZ-${userInfo.mobile.slice(-5)}-GEN`;
      setSubmittedNo(nextNo);

      // Store in local storage history logs
      const newEntry = {
        enquiry_no: nextNo,
        created_at: new Date().toISOString()
      };

      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const updatedHistory = [newEntry, ...existing];

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
      setHistory(updatedHistory);

      if (sendWhatsApp) {
        window.open(
          `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage()}`,
          "_blank"
        );
      }

      // Clear cart
      localStorage.removeItem("cart");
      setCart([]);
      window.dispatchEvent(new Event("cart-updated"));
      setSuccess(true);
    } catch (e) {
      console.error(e);
      alert("Failed to submit quotation enquiry. Please verify your connection.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SUCCESS SCREEN ---------------- */

  if (success) {
    return (
      <div className="min-h-[70vh] bg-slate-50 flex items-center justify-center select-none px-4">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-8 shadow-md text-center space-y-6">
          <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto animate-bounce">
            <CheckCircle2 className="w-10 h-10" />
          </div>

          <div>
            <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight mb-2">Quotation Requested!</h2>
            <p className="text-sm text-slate-500 font-medium">
              Your request has been successfully registered. A representative will contact you shortly.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 text-left">
            <span className="text-[10px] font-bold text-slate-400 tracking-wider block uppercase mb-1">Enquiry Reference</span>
            <span className="text-base font-extrabold text-slate-800 font-mono tracking-wide">{submittedNo}</span>
          </div>

          <div className="pt-2">
            <Link
              to="/"
              className="bg-slate-900 text-white font-bold text-xs uppercase tracking-wider py-4 px-6 rounded-2xl w-full block transition-all duration-200 active:scale-95 shadow-md"
            >
              Continue Browsing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 select-none pb-12">
      <div className="max-w-lg mx-auto px-4 py-8">

        {/* Header link */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 uppercase tracking-wider mb-6 hover:text-blue-700 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Catalog</span>
        </Link>

        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-6 flex items-center gap-2">
          <span>Quotation Cart</span>
          {cart.length > 0 && (
            <span className="bg-blue-600 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
              {cart.length} Items
            </span>
          )}
        </h1>

        {cart.length === 0 ? (
          <div className="space-y-4">
            <div className="text-center py-16 px-4 bg-white rounded-3xl border border-slate-200 shadow-sm">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Your cart is empty</h3>
              <p className="text-sm text-slate-400 mt-1 max-w-[15rem] mx-auto mb-6">
                Add some high-performance electronics to build a quotation request.
              </p>
              <Link
                to="/"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-widest py-3.5 px-6 rounded-2xl transition-all duration-200 active:scale-95 shadow-sm"
              >
                Explore Products
              </Link>
            </div>

            {/* Custom product form when cart is empty */}
            <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-3">
              {!showCustomForm ? (
                <div className="text-center py-2">
                  <p className="text-xs text-slate-400 font-medium mb-3">
                    Can't find the item you are looking for in our catalog?
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCustomForm(true)}
                    className="w-full py-3.5 px-4 border border-dashed border-slate-300 rounded-2xl text-xs font-bold text-slate-500 uppercase tracking-widest hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/20 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4 animate-bounce" />
                    <span>Request Custom Product / Service</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAddCustom} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                      <span>Custom Product Details</span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowCustomForm(false)}
                      className="text-[10px] font-bold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>

                  <input
                    placeholder="Product Name (e.g. Custom 2U Server Chassis)"
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    required
                    className="w-full border border-slate-200 rounded-xl px-4 py-3
                      focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                      transition-all duration-300 font-medium text-slate-700 text-sm bg-white"
                  />

                  <textarea
                    placeholder="Describe what you need (specifications, dimensions, custom features)..."
                    value={customDesc}
                    onChange={e => setCustomDesc(e.target.value)}
                    rows={2.5}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3
                      focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                      transition-all duration-300 font-medium text-slate-700 text-sm bg-white resize-none"
                  />

                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider py-3.5 px-4 rounded-xl w-full transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    Add to Cart
                  </button>
                </form>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            {/* Cart Grid */}
            <div className="space-y-3">
              {cart.map(p => (
                <div
                  key={p.id}
                  className="flex justify-between items-center bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
                >
                  <div className="flex-1 pr-4 min-w-0">
                    <div className="font-bold text-slate-800 text-sm tracking-tight leading-snug line-clamp-1">{p.name}</div>
                    {p.is_custom && (
                      <div className="flex flex-col gap-1 mt-1">
                        <span className="self-start text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 tracking-wider uppercase">
                          Custom Item
                        </span>
                        {p.product_desc && (
                          <p className="text-xs text-slate-500 font-medium italic leading-relaxed line-clamp-2">
                            {p.product_desc}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-100 rounded-xl px-2 py-1 shrink-0">
                    <button
                      onClick={() => updateQty(p.id, -1)}
                      className="text-red-600 font-extrabold text-base w-7 h-7 flex items-center justify-center bg-white border border-slate-100 rounded-lg shadow-sm active:scale-90"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={p.quantity}
                      min="1"
                      onChange={e => handleQtyChange(p.id, e.target.value)}
                      onBlur={e => handleQtyBlur(p.id, e.target.value)}
                      className="font-extrabold text-slate-800 text-sm w-10 text-center bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      onClick={() => updateQty(p.id, 1)}
                      className="text-green-600 font-extrabold text-base w-7 h-7 flex items-center justify-center bg-white border border-slate-100 rounded-lg shadow-sm active:scale-90"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Custom Product Form/Toggler for Non-Empty Cart */}
            <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-3">
              {!showCustomForm ? (
                <button
                  type="button"
                  onClick={() => setShowCustomForm(true)}
                  className="w-full py-3 px-4 border border-dashed border-slate-300 rounded-2xl text-xs font-bold text-slate-500 uppercase tracking-widest hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/20 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4 text-purple-500" />
                  <span>Add Another Custom Request</span>
                </button>
              ) : (
                <form onSubmit={handleAddCustom} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                      <span>Custom Product Details</span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowCustomForm(false)}
                      className="text-[10px] font-bold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>

                  <input
                    placeholder="Item Name (e.g. 10m Copper Wire)"
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    required
                    className="w-full border border-slate-200 rounded-xl px-4 py-3
                      focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                      transition-all duration-300 font-medium text-slate-700 text-sm bg-white"
                  />

                  <textarea
                    placeholder="Description, notes, brand preferences, or special requests..."
                    value={customDesc}
                    onChange={e => setCustomDesc(e.target.value)}
                    rows={2}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3
                      focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                      transition-all duration-300 font-medium text-slate-700 text-sm bg-white resize-none"
                  />

                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider py-3 px-4 rounded-xl w-full transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    Add to Cart
                  </button>
                </form>
              )}
            </div>

            {/* Submission Form */}
            <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-800 text-sm tracking-tight mb-2">Request Details</h3>

              <div className="relative">
                <User className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  placeholder="Your Full Name"
                  value={userInfo.name}
                  onChange={e => setUserInfo({ ...userInfo, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-3
                    focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                    transition-all duration-300 font-medium text-slate-700 text-sm bg-white"
                />
              </div>

              <div className="relative">
                <Phone className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  placeholder="Mobile Number"
                  value={userInfo.mobile}
                  onChange={e => setUserInfo({ ...userInfo, mobile: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-3
                    focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                    transition-all duration-300 font-medium text-slate-700 text-sm bg-white"
                />
              </div>

              <label className="flex items-center gap-2.5 text-xs text-slate-500 font-bold uppercase tracking-wider cursor-pointer py-1 select-none">
                <input
                  type="checkbox"
                  checked={sendWhatsApp}
                  onChange={e => setSendWhatsApp(e.target.checked)}
                  className="w-4.5 h-4.5 text-blue-600 border-slate-200 rounded-lg focus:ring-blue-500"
                />
                <span>Send copy on WhatsApp</span>
              </label>

              <div className="pt-2">
                <button
                  onClick={handleSubmit}
                  disabled={loading || cart.length === 0}
                  className={`w-full py-4 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all duration-200 shadow-md ${loading || cart.length === 0
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed shadow-none"
                    : "bg-blue-600 hover:bg-blue-700 text-white active:scale-95"
                    }`}
                >
                  <Send className="w-4 h-4" />
                  <span>{loading ? "Registering..." : "Submit Quote Request"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ENQUIRY HISTORY PANEL */}
        {history.length > 0 && (
          <div className="border-t border-slate-200 pt-6">
            <h3 className="font-extrabold text-slate-800 text-sm tracking-tight mb-4 flex items-center gap-2">
              <History className="w-4 h-4 text-slate-400" />
              <span>Previous Quotation Requests</span>
            </h3>

            <div className="grid gap-3">
              {history.map((e, i) => (
                <div
                  key={i}
                  className="border border-slate-200 rounded-2xl px-4 py-3.5 bg-white shadow-sm flex items-center justify-between"
                >
                  <div>
                    <div className="font-extrabold text-slate-700 text-sm font-mono tracking-wide">{e.enquiry_no}</div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                      {new Date(e.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                      })}
                    </div>
                  </div>
                  <span className="bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-blue-100">
                    Registered
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
